import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiTrendingUp, FiTarget, FiAlertCircle, FiCalendar } from 'react-icons/fi';
import { Button } from '@/components/common/Button';
import { mockWeeklyUpdate } from '@/data/mockData';

const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-shrink: 0;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  flex: 1;
`;

const WeekBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};
  font-weight: ${({ theme }) => theme.fontWeights.medium};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ContentGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
`;

const Section = styled(motion.div)<{ color: string }>`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  border-left: 4px solid ${({ color }) => color};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-shrink: 0;
`;

const SectionIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ color }) => color}15;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ color }) => color};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const ItemsList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.muted};
    }
  }
`;

const Item = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  line-height: 1.6;
`;

const ItemBullet = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color};
  margin-top: 8px;
  flex-shrink: 0;
`;

const ItemText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.onSurface};
  margin: 0;
`;

const LargeSection = styled(Section)`
  grid-column: span 2;
`;

const DecisionCard = styled(motion.div)`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
      : 'rgba(255, 255, 255, 0.6)'};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid ${({ theme }) => theme.colors.error}40;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
`;

const DecisionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.error}15;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.error};
  }
`;

const DecisionText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.onSurface};
  margin: 0;
  line-height: 1.6;
`;

export const WeeklyUpdatePage = () => {
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <BackButton variant="outline" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back
        </BackButton>
        <Title>Weekly Update</Title>
        <WeekBadge>
          <FiCalendar />
          Week of {mockWeeklyUpdate.weekOf}
        </WeekBadge>
      </Header>

      <ContentGrid>
        {/* Wins Section */}
        <Section
          color="#10b981"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
        >
          <SectionHeader>
            <SectionIcon color="#10b981">
              <FiTrendingUp />
            </SectionIcon>
            <SectionTitle>This Week's Wins</SectionTitle>
          </SectionHeader>
          <ItemsList>
            {mockWeeklyUpdate.wins.map((win, index) => (
              <Item
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
              >
                <ItemBullet color="#10b981" />
                <ItemText>{win}</ItemText>
              </Item>
            ))}
          </ItemsList>
        </Section>

        {/* Upcoming Focus Section */}
        <Section
          color="#2563eb"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <SectionHeader>
            <SectionIcon color="#2563eb">
              <FiTarget />
            </SectionIcon>
            <SectionTitle>Upcoming Focus</SectionTitle>
          </SectionHeader>
          <ItemsList>
            {mockWeeklyUpdate.upcomingFocus.map((focus, index) => (
              <Item
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.05 }}
              >
                <ItemBullet color="#2563eb" />
                <ItemText>{focus}</ItemText>
              </Item>
            ))}
          </ItemsList>
        </Section>

        {/* Decisions Required Section */}
        <LargeSection
          color="#ef4444"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <SectionHeader>
            <SectionIcon color="#ef4444">
              <FiAlertCircle />
            </SectionIcon>
            <SectionTitle>Decisions Required</SectionTitle>
          </SectionHeader>
          <ItemsList>
            {mockWeeklyUpdate.decisionsRequired.map((decision, index) => (
              <DecisionCard
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <DecisionIcon>
                  <FiAlertCircle />
                </DecisionIcon>
                <DecisionText>{decision}</DecisionText>
              </DecisionCard>
            ))}
          </ItemsList>
        </LargeSection>
      </ContentGrid>
    </Container>
  );
};
